import React, { useState, useEffect, useCallback } from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { SharedFilterValues } from "./SharedFilters"
import DocumentPreviewDialog from "./DocumentPreviewDialog"
import * as documentsService from "../../services/documentsService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"

interface DocumentsTabProps {
    filters: SharedFilterValues
    companyId: string
}

const TYPE_LABEL_KEYS: Record<string, string> = {
    invoice: "exportsComponents.documentsTab.documentTypes.invoice",
    credit_note: "exportsComponents.documentsTab.documentTypes.creditNote",
    receipt: "exportsComponents.documentsTab.documentTypes.receipt",
    quotation: "exportsComponents.documentsTab.documentTypes.quotation",
    sales_order: "exportsComponents.documentsTab.documentTypes.salesOrder",
    proforma: "exportsComponents.documentsTab.documentTypes.proforma",
}

const DOCUMENT_TYPE_OPTIONS = [
    {
        value: "receipt",
        labelKey: "exportsComponents.documentsTab.documentTypes.receipt",
    },
    {
        value: "invoice",
        labelKey: "exportsComponents.documentsTab.documentTypes.invoice",
    },
    {
        value: "credit_note",
        labelKey: "exportsComponents.documentsTab.documentTypes.creditNote",
    },
]

const TYPE_CHIP_COLORS: Record<
    string,
    "primary" | "warning" | "success" | "secondary" | "info" | "default"
> = {
    invoice: "primary",
    credit_note: "warning",
    receipt: "success",
    quotation: "secondary",
    sales_order: "info",
    proforma: "default",
}

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)

const DocumentsTab: React.FC<DocumentsTabProps> = ({ filters, companyId }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [documents, setDocuments] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // Local filters
    const [documentType, setDocumentType] = useState("")
    const [documentNumberSearch, setDocumentNumberSearch] = useState("")
    const [customerSearch, setCustomerSearch] = useState("")

    // Preview
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewDocNumber, setPreviewDocNumber] = useState("")
    const [previewDocType, setPreviewDocType] = useState<string | undefined>(
        undefined
    )

    // Invoice creation
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
    const [invoiceReceiptId, setInvoiceReceiptId] = useState<string | null>(
        null
    )
    const [invoiceLoading, setInvoiceLoading] = useState(false)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({ open: false, message: "", severity: "success" })

    const loadDocuments = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await documentsService.searchDocuments(companyId, {
                outletId: filters.outletId || undefined,
                registerId: filters.registerId || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                documentType: documentType || undefined,
                number: documentNumberSearch || undefined,
                customerSearch: customerSearch || undefined,
                limit: rowsPerPage,
                offset: page * rowsPerPage,
            })
            setDocuments(result.documents || [])
            setTotal(result.total || 0)
        } catch (err) {
            console.error("Failed to load documents:", err)
        } finally {
            setLoading(false)
        }
    }, [
        companyId,
        filters,
        documentType,
        documentNumberSearch,
        customerSearch,
        page,
        rowsPerPage,
    ])

    useEffect(() => {
        loadDocuments()
    }, [loadDocuments])

    // Reset page when filters change
    useEffect(() => {
        setPage(0)
    }, [filters, documentType, documentNumberSearch, customerSearch])

    const handleCreateInvoice = async () => {
        if (!companyId || !invoiceReceiptId) return
        setInvoiceLoading(true)
        try {
            const invoice = await documentsService.createInvoiceFromReceipt(
                companyId,
                invoiceReceiptId
            )
            setInvoiceDialogOpen(false)
            setSnackbar({
                open: true,
                message: String(
                    t(
                        "exportsComponents.documentsTab.notifications.invoiceCreated"
                    )
                ),
                severity: "success",
            })
            navigate(`/documents/${invoice.id}`)
        } catch (error: any) {
            console.error("Failed to create invoice:", error)
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    String(
                        t(
                            "exportsComponents.documentsTab.notifications.invoiceFailed"
                        )
                    ),
                severity: "error",
            })
        } finally {
            setInvoiceLoading(false)
        }
    }

    const handlePreview = async (doc: any) => {
        setPreviewDocNumber(
            doc.number || String(t("exportsComponents.common.unnumbered"))
        )
        setPreviewDocType(doc.document_type)
        setPreviewOpen(true)
        setPreviewLoading(true)
        setPreviewHtml(null)
        try {
            const html = await documentsService.getHtmlPreview(
                companyId,
                doc.id
            )
            setPreviewHtml(html)
        } catch (err) {
            console.error("Failed to load preview:", err)
            setPreviewHtml(null)
        } finally {
            setPreviewLoading(false)
        }
    }

    return (
        <Box>
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    mb: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(180px, 220px) minmax(180px, 1fr) minmax(220px, 1fr)",
                    },
                }}
            >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel shrink>
                        {t(
                            "exportsComponents.documentsTab.filters.documentType"
                        )}
                    </InputLabel>
                    <Select
                        value={documentType}
                        label={String(
                            t(
                                "exportsComponents.documentsTab.filters.documentType"
                            )
                        )}
                        displayEmpty
                        renderValue={(value) => {
                            const selectedValue = String(value || "")
                            if (!selectedValue)
                                return t("exportsComponents.common.all")
                            const selectedOption = DOCUMENT_TYPE_OPTIONS.find(
                                (option) => option.value === selectedValue
                            )
                            return selectedOption
                                ? t(selectedOption.labelKey)
                                : t("exportsComponents.common.all")
                        }}
                        onChange={(e) => setDocumentType(e.target.value)}
                    >
                        <MenuItem value="">
                            {t("exportsComponents.common.all")}
                        </MenuItem>
                        {DOCUMENT_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(option.labelKey)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label={String(
                        t(
                            "exportsComponents.documentsTab.filters.documentNumber"
                        )
                    )}
                    value={documentNumberSearch}
                    onChange={(e) => setDocumentNumberSearch(e.target.value)}
                />

                <TextField
                    size="small"
                    label={String(
                        t("exportsComponents.documentsTab.filters.customer")
                    )}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                {t(
                                    "exportsComponents.documentsTab.columns.number"
                                )}
                            </TableCell>
                            <TableCell>
                                {t(
                                    "exportsComponents.documentsTab.columns.type"
                                )}
                            </TableCell>
                            <TableCell>
                                {t(
                                    "exportsComponents.documentsTab.columns.date"
                                )}
                            </TableCell>
                            <TableCell>
                                {t(
                                    "exportsComponents.documentsTab.columns.customer"
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.ht")}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.vat")}
                            </TableCell>
                            <TableCell align="right">
                                {t("exportsComponents.common.ttc")}
                            </TableCell>
                            <TableCell align="center">
                                {t(
                                    "exportsComponents.documentsTab.columns.actions"
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && documents.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : documents.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    align="center"
                                    sx={{ py: 4 }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t(
                                            "exportsComponents.documentsTab.empty"
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            documents.map((doc) => (
                                <TableRow
                                    key={doc.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() =>
                                        navigate(`/documents/${doc.id}`)
                                    }
                                >
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                        >
                                            {doc.number ||
                                                t(
                                                    "exportsComponents.common.unnumbered"
                                                )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={
                                                TYPE_LABEL_KEYS[
                                                    doc.document_type
                                                ]
                                                    ? t(
                                                          TYPE_LABEL_KEYS[
                                                              doc.document_type
                                                          ]
                                                      )
                                                    : doc.document_type
                                            }
                                            color={
                                                TYPE_CHIP_COLORS[
                                                    doc.document_type
                                                ] || "default"
                                            }
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(
                                            doc.created_at
                                        ).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        {doc.customer_snapshot?.name ||
                                            doc.customer_snapshot
                                                ?.company_name ||
                                            "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {fmtC(
                                            doc.totals?.net_total || 0,
                                            doc.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {fmtC(
                                            doc.totals?.vat_total || 0,
                                            doc.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {fmtC(
                                            doc.totals?.gross_total || 0,
                                            doc.currency
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 0.5,
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Tooltip
                                                title={String(
                                                    t(
                                                        "exportsComponents.documentsTab.actions.preview"
                                                    )
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handlePreview(doc)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {doc.document_type === "receipt" &&
                                                doc.status === "validated" &&
                                                !doc.links
                                                    ?.related_invoice_id && (
                                                    <Tooltip
                                                        title={String(
                                                            t(
                                                                "exportsComponents.documentsTab.actions.createInvoice"
                                                            )
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => {
                                                                setInvoiceReceiptId(
                                                                    doc.id
                                                                )
                                                                setInvoiceDialogOpen(
                                                                    true
                                                                )
                                                            }}
                                                        >
                                                            <ReceiptLongIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10))
                        setPage(0)
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage={String(t("common.rowsPerPage"))}
                    labelDisplayedRows={({ from, to, count }) =>
                        String(
                            t("common.paginationDisplayedRows", {
                                from,
                                to,
                                count: count === -1 ? to : count,
                            })
                        )
                    }
                />
            </TableContainer>

            <DocumentPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                html={previewHtml}
                loading={previewLoading}
                documentNumber={previewDocNumber}
                documentType={previewDocType}
            />

            <Dialog
                open={invoiceDialogOpen}
                onClose={() => setInvoiceDialogOpen(false)}
            >
                <DialogTitle>
                    {t("exportsComponents.documentsTab.invoiceDialog.title")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {t(
                            "exportsComponents.documentsTab.invoiceDialog.message"
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInvoiceDialogOpen(false)}>
                        {t(
                            "exportsComponents.documentsTab.invoiceDialog.cancel"
                        )}
                    </Button>
                    <Button
                        onClick={handleCreateInvoice}
                        color="primary"
                        variant="contained"
                        disabled={invoiceLoading}
                    >
                        {t(
                            "exportsComponents.documentsTab.invoiceDialog.confirm"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default DocumentsTab
